<template>
  <div :id="containerId"></div>
</template>

<script setup>
import { watch, nextTick } from 'vue'

import Highcharts from 'highcharts'
import * as Sentry from '@sentry/vue'

import { convertDateToFormattedString } from '@/utils/Utility.ts'
import { useSessionValues } from '@/composables/useSessionValues'

const props = defineProps({
  containerId: {
    type: String,
    required: true,
  },
  seriesData: {
    type: Array,
    default() {
      return []
    },
  },
  studioPlus: {
    type: Number,
    default: null,
  },
  isMobile: {
    type: Boolean,
    required: true,
  },
})

const { language } = useSessionValues()
const drawChart = (containerId, data, studioPlus, isMobile) => {
  if (!containerId || !data) return

  try {
    // without nextTick, highcharts may use the container before it exists in the DOM, it is important!
    nextTick(() => {
      // if for some reason the container is not yet mounted, go back.
      if (!document.getElementById(containerId)) return
      Highcharts.chart(containerId, {
        chart: {
          backgroundColor: null,
          type: 'spline',
          plotBorderWidth: 0,
          alignTicks: false,
        },
        title: {
          text: null,
        },
        credits: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          spline: {
            marker: {
              symbol: 'circle',
              radius: 4,
              lineWidth: 1,
            },
          },
        },
        yAxis: {
          title: false,
          gridLineWidth: 1,
          tickLength: 0,
          gridLineColor: '#C4E3F4',
        },
        xAxis: {
          visible: true,
          gridLineWidth: 1,
          tickLength: 0,
          ordinal: false,
          type: 'datetime',
          gridLineColor: '#C4E3F4',
          lineColor: '#C4E3F4',
          tickColor: '#C4E3F4',
          crosshair: {
            color: '#C4E3F4',
            dashStyle: 'solid',
          },
          labels: {
            useHTML: true,
            formatter: function () {
              const label = convertDateToFormattedString(new Date(this.value * 1000), language.value, {
                month: 'short',
                day: '2-digit',
              })
              return `<span>${label}</span>`
            },
            style: {
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            },
            rotation: isMobile ? -45 : 0,
            align: 'center',
          },
          plotLines: [
            {
              color: '#000',
              width: 2,
              zIndex: 1,
              value: studioPlus,
              label: {
                useHTML: true,
                align: 'center',
                x: -7,
                y: 4,
                formatter: function () {
                  return `<div style="width: 0;height: 0;border-top: 5px solid transparent; border-bottom: 5px solid transparent; border-left: 10px solid #000;"/>`
                },
              },
            },
          ],
        },
        tooltip: {
          useHTML: true,
          hideDelay: 0,
          shared: true,

          formatter: function () {
            const html = ({ date, series }) => `<div>
                  <div>${date}</div>
                  ${series}
                </div>`

            const series = ({ name, value }) => `<div>
                  <span>${name}: </span><span>${value}</span>
                </div>`

            let aggregationType
            let renderedSeries = ''
            for (let i = 0; i < this.points.length; i++) {
              let point = this.points[i]
              aggregationType = point.series.userOptions.aggregationType
              if (point.series.userOptions.internalName === 'clickThroughRate') {
                renderedSeries += series({
                  name: language.value === 'fr-FR' ? `${point.series.name} ` : `${point.series.name}`,
                  internalName: point.series.userOptions.internalName,
                  value:
                    language.value === 'tr-TR'
                      ? `%${Intl.NumberFormat(language.value).format(point.y).toString()}`
                      : `${Intl.NumberFormat(language.value).format(point.y).toString()}%`,
                })
              } else {
                renderedSeries += series({
                  name: point.series.name,
                  internalName: point.series.userOptions.internalName,
                  value: Intl.NumberFormat(language.value).format(point.y),
                })
              }
            }

            if (aggregationType === 'weekly') {
              const startDate = convertDateToFormattedString(new Date(this.x * 1000), language.value)
              const endDate = convertDateToFormattedString(new Date(this.x + 6 * 24 * 60 * 60 * 1000), language.value)
              return html({
                date: `${startDate} - ${endDate}`,
                series: renderedSeries,
              })
            } else {
              const date = convertDateToFormattedString(new Date(this.x * 1000), language.value)
              return html({
                date,
                series: renderedSeries,
              })
            }
          },
        },
        series: data,
      })
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

watch(
  () => props,
  newProps => {
    if (newProps.seriesData?.length > 0 && newProps.containerId) {
      drawChart(newProps.containerId, newProps.seriesData, props.studioPlus, props.isMobile)
    }
  },
  { deep: true, immediate: true }
)
watch(language, (currentValue, oldValue) => {
  if (currentValue !== oldValue) {
    drawChart(props.containerId, props.data, props.studioPlus, props.isMobile)
  }
})
</script>
